import * as React from "react";
import {observer} from "mobx-react";
import {useNavigate} from "react-router-dom";
import {Box, MenuItem, TextField} from "@mui/material";
import {DndContext} from "@dnd-kit/core";
import {useApplication} from "../../../ApplicationProvider";
import {useNavigatorFeature} from "../../../useFeature";
import LayoutContentWrapper from "../../../components/layout/LayoutContentWrapper";
import LayoutContentTitle from "../../../components/layout/LayoutContentTitle";
import LeadPipelineFeature from "./LeadPipelineFeature";
import LeadPipelineColumn from "./LeadPipelineColumn";
import LeadPipelineCard from "./LeadPipelineCard";
import LeadPipelineDraggedCard from "./LeadPipelineDraggedCard";
import {styles} from "./LeadPipelineStyles";
import leadStageTitleOf from "../leadStageTitleOf";

function LeadPipelineScreen() {
    const application = useApplication();
    const navigator = useNavigate();
    const style = styles();
    const feature = useNavigatorFeature(() => new LeadPipelineFeature(application), navigator);
    const isFeatureReady = feature && feature.loaded;

    const outOfFlowStage = 'STAND_BY';
    const pipelineAgeOptions = [
        {value: '', label: 'Cualquiera'},
        {value: 'LAST_7_DAYS', label: 'Últimos 7 días'},
        {value: 'LAST_30_DAYS', label: 'Últimos 30 días'},
        {value: 'MORE_THAN_30_DAYS', label: 'Más de 30 días'},
    ];

    const dragStarted = ({active}) => feature.startDraggingFromStage(active.data.current.stage);

    const dragEnded = ({active, over}) => {
        feature.stopDragging();
        const targetStage = over ? over.id : null;
        feature.moveLeadTo(active.id, active.data.current.stage, targetStage);
    };

    const filterOptionItems = (options) => [
        <MenuItem key="any" value="">Todos</MenuItem>,
        ...options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>),
    ];

    const filterBar = () => (
        <Box sx={style.filterBar}>
            <TextField
                size="small"
                label="Nombre"
                value={feature.filters.fantasyName}
                onChange={(event) => feature.editFantasyNameFilter(event.target.value)}
                sx={style.filterField}
            />
            <TextField
                select
                size="small"
                label="Red de origen"
                value={feature.filters.howTheyKnewUs}
                onChange={(event) => feature.editHowTheyKnewUsFilter(event.target.value)}
                sx={style.filterField}
            >
                {filterOptionItems(feature.howTheyKnewUsNames)}
            </TextField>
            <TextField
                select
                size="small"
                label="Antigüedad"
                value={feature.filters.pipelineAge}
                onChange={(event) => feature.editPipelineAgeFilter(event.target.value)}
                sx={style.filterField}
            >
                {pipelineAgeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
            </TextField>
            {feature.showsCommercialChannelFilter && (
                <TextField
                    select
                    size="small"
                    label="Canal"
                    value={feature.filters.commercialChannel}
                    onChange={(event) => feature.editCommercialChannelFilter(event.target.value)}
                    sx={style.filterField}
                >
                    {filterOptionItems(feature.commercialChannelsToChooseFrom)}
                </TextField>
            )}
            {feature.showsResponsibleFilter && (
                <TextField
                    select
                    size="small"
                    label="Responsable"
                    value={feature.filters.responsibleEmail}
                    onChange={(event) => feature.editResponsibleEmailFilter(event.target.value)}
                    sx={style.filterField}
                >
                    <MenuItem value="">Todos</MenuItem>
                    {feature.responsiblesToChooseFrom.map((responsible) => (
                        <MenuItem key={responsible.email} value={responsible.email}>{responsible.name}</MenuItem>
                    ))}
                </TextField>
            )}
        </Box>
    );

    const column = (pipelineColumn) => {
        const isOutOfFlow = pipelineColumn.stage === outOfFlowStage;
        return (
            <LeadPipelineColumn
                key={pipelineColumn.stage}
                stage={pipelineColumn.stage}
                title={leadStageTitleOf(pipelineColumn.stage)}
                caption={isOutOfFlow ? 'fuera del flujo' : undefined}
                leadsCount={pipelineColumn.leads.length}
                acceptsDrop={feature.acceptsDropOfLeadDraggedOverStage(pipelineColumn.stage)}
            >
                {pipelineColumn.leads.map((lead) => (
                    <LeadPipelineCard
                        key={lead.id}
                        lead={lead}
                        stage={pipelineColumn.stage}
                        isDraggable={feature.isLeadFromStageDraggable(pipelineColumn.stage)}
                        showsCommercialChannel={feature.showsCommercialChannelFilter}
                    />
                ))}
            </LeadPipelineColumn>
        );
    };

    const board = () => (
        <DndContext onDragStart={dragStarted} onDragEnd={dragEnded} onDragCancel={() => feature.stopDragging()}>
            <Box sx={style.board}>
                {feature.columns.filter((pipelineColumn) => pipelineColumn.stage !== outOfFlowStage).map(column)}
                <Box sx={style.boardSeparator}/>
                {feature.columns.filter((pipelineColumn) => pipelineColumn.stage === outOfFlowStage).map(column)}
            </Box>
            <LeadPipelineDraggedCard showsCommercialChannel={feature.showsCommercialChannelFilter}/>
        </DndContext>
    );

    const content = () => (
        <Box sx={style.container}>
            <LayoutContentTitle title="Pipeline de leads"/>
            {filterBar()}
            {board()}
        </Box>
    );

    return <LayoutContentWrapper isLoading={!isFeatureReady} renderContent={() => content()}/>;
}

export default observer(LeadPipelineScreen);
